import { useQuery } from '@tanstack/react-query';
import { concat } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { fetchLocations } from 'src/hooks';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { ILocationTag } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { mapAllLocationTags } from './helper';

export const useGetAllLocationTags = (userRole?: AuthClassification | null) =>
  useQuery({
    queryKey: queryKeys.locationTags.all(),
    queryFn: fetchAllLocationTags,
    enabled: userRole !== AuthClassification.OWNER_OPERATOR,
  });

const fetchAllLocationTags = async () => {
  const url = `/locationTags?pageSize=0`;

  const [response, locations] = await Promise.all([
    requestCCApi({ url }),
    queryClient.fetchQuery({
      queryKey: queryKeys.locations.all(),
      queryFn: () => fetchLocations(),
    }),
  ]);

  if (response.data) {
    return mapAllLocationTags(response.data, locations);
  }

  return [];
};

export const useGetPaginatedLocationTagsWithLocationCount = (
  page: number,
  filters: string[] = [],
  pageSize: number,
  userRole?: AuthClassification | null,
) => {
  const queryParams = concat(
    [`page=${page}`],
    [`pageSize=${pageSize}`],
    ['count=true'],
    filters,
  );

  return useQuery({
    queryKey: queryKeys.locationTags.pagination([page, ...filters]),
    queryFn: async () => {
      const response = await requestCCApi({
        url: `/getLocationTagsWithLocationCount${generateQuery(
          [],
          queryParams,
        )}`,
      });

      return {
        locations: response.data as ILocationTag[],
        totalPages: response.totalPages ?? 1,
        totalCount: response.totalCount ?? 0,
      };
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: userRole !== AuthClassification.OWNER_OPERATOR,
  });
};
